import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Map, Camera, TrendingUp, Users, CheckCircle, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const onboardingSteps = [
  {
    icon: Map,
    title: "Welcome to FixMyHood",
    description: "A community platform to report and track infrastructure issues in Cape Town",
    color: "text-emerald-600",
    bg: "bg-emerald-100",
  },
  {
    icon: Camera,
    title: "Report Issues Easily",
    description: "Snap a photo, add a description, and we'll automatically geotag the location",
    color: "text-blue-600",
    bg: "bg-blue-100",
  },
  {
    icon: TrendingUp,
    title: "Engage with Your Community",
    description: "Upvote important issues, comment with updates, and track progress in real-time",
    color: "text-purple-600",
    bg: "bg-purple-100",
  },
  {
    icon: Users,
    title: "Make a Real Impact",
    description: "Your reports help city workers and contractors fix problems faster",
    color: "text-amber-600",
    bg: "bg-amber-100",
  },
];

export default function Onboarding({ isOpen, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      localStorage.setItem('onboarding_completed', 'true');
      onComplete();
    }
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding_completed', 'true');
    onComplete();
  };

  const step = onboardingSteps[currentStep];
  const Icon = step.icon;

  return (
    <Dialog open={isOpen} onOpenChange={handleSkip}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="sr-only">Welcome to FixMyHood</DialogTitle>
        </DialogHeader>

        <div className="py-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Icon */}
              <div className={`w-20 h-20 ${step.bg} rounded-full flex items-center justify-center mx-auto mb-6`}>
                <Icon className={`w-10 h-10 ${step.color}`} />
              </div>

              {/* Content */}
              <h2 className="text-2xl font-bold text-center text-gray-900 mb-3">
                {step.title}
              </h2>
              <p className="text-center text-gray-600 mb-8 px-4">
                {step.description}
              </p>

              {/* Progress Dots */}
              <div className="flex justify-center gap-2 mb-8">
                {onboardingSteps.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 rounded-full transition-all ${
                      index === currentStep
                        ? 'w-8 bg-emerald-600'
                        : 'w-2 bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleSkip}
                  className="flex-1"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
                >
                  {currentStep === onboardingSteps.length - 1 ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Get Started
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}